/**
 * conversationErrorCopy — turns a raw transport `Error` into friendly,
 * user-facing copy for the inline chat error notice (ANA-194).
 *
 * The transport layer (`useTextConversation`) throws technical messages —
 * `"Message failed: 500"`, `"Conversation stream stalled (no data received)"`,
 * `"Conversation produced no response"`, `"Failed to fetch"`, etc. Those are
 * useful in logs but not for the user, so we collapse them into a small set of
 * plain-language messages. Pure + side-effect-free for unit testing.
 */

export interface ConversationErrorCopy {
  title: string;
  message: string;
}

const GENERIC: ConversationErrorCopy = {
  title: 'Something went wrong',
  message: 'Something went wrong. Try again.',
};

// Anaka held the connection open but never produced anything (idle-stall or an
// explicit per-step timeout).
const TIMEOUT = /stall|no data|took too long|timed out|timeout/i;
// The stream closed cleanly without any assistant content (see
// `useTextConversation` empty-response detection).
const EMPTY = /no response|produced no response|did ?n['’]?t reply/i;
// Network failure, non-2xx after backoff, or a stream that closed before a
// session/handshake — all "we couldn't complete the round-trip".
const TRANSPORT =
  /failed: ?\d|failed to fetch|networkerror|load failed|transport failed|did not start|closed the stream|network/i;

/**
 * Map an error to `{ title, message }`. Order matters: the more specific
 * timeout / empty buckets are checked before the broad transport bucket so a
 * stall isn't mislabelled as a generic connection problem.
 */
export function conversationErrorCopy(error: Error): ConversationErrorCopy {
  const raw = error?.message ?? '';

  if (TIMEOUT.test(raw)) {
    return {
      title: 'Anaka took too long',
      message: 'Anaka took too long to respond. Try again.',
    };
  }
  if (EMPTY.test(raw)) {
    return {
      title: 'No response',
      message: "Anaka didn't reply. Try again.",
    };
  }
  if (TRANSPORT.test(raw)) {
    return {
      title: "Couldn't reach Anaka",
      message: 'Couldn’t reach Anaka. Check your connection and try again.',
    };
  }
  return GENERIC;
}
